import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceArea } from 'recharts';
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';
import CartaoExpansivel from './CartaoExpansivel';
import AvisoDadosDesatualizados from './AvisoDadosDesatualizados';
import { formatarKg, formatarNumero, formatarRendimento } from '../../utils/formatters';

// Defeso (piracema) da bacia do Paraná: 1º de novembro ao fim de fevereiro. O dia final é 29 de
// propósito: as faixas são montadas comparando datas AAAA-MM-DD como texto, então "02-29" cobre
// o dia 29/fev nos anos bissextos e, nos demais, é equivalente a 28/fev (não existe período nesse dia).
// Mudar aqui muda a faixa sombreada e a legenda do gráfico.
export const PIRACEMA = {
  rotulo: 'Piracema (bacia do Paraná)',
  inicio: { mes: 11, dia: 1 },
  fim: { mes: 2, dia: 29 },
};

const JANELA_MEDIA_MOVEL = 7;

type Agregacao = 'dia' | 'semana' | 'mes';
type Metrica = 'kg' | 'rendimento' | 'registros' | 'esforco' | 'pescadores' | 'semAtividade';

interface PontoSerie {
  periodo: string; // AAAA-MM-DD (início do período)
  kg: number;
  rendimento: number | null;
  registros: number;
  esforco: number; // pescador-dias (denominador do rendimento)
  kgBase: number; // kg dos registros com pescadores informados (numerador do rendimento)
  pescadoresAtivos: number; // pescadores distintos com registro no período
  // Só na métrica "Dias sem atividade" (vem de /stats/interrupcoes): pescador-dias sem atividade e, desses, por motivo ambiental.
  semAtividade?: number;
  ambientais?: number;
}

const AGREGACOES: { valor: Agregacao; rotulo: string }[] = [
  { valor: 'dia', rotulo: 'Dia' },
  { valor: 'semana', rotulo: 'Semana' },
  { valor: 'mes', rotulo: 'Mês' },
];

const METRICAS: { valor: Metrica; rotulo: string; unidade: string }[] = [
  { valor: 'kg', rotulo: 'Produção (kg)', unidade: 'kg' },
  { valor: 'rendimento', rotulo: 'Rendimento (kg/pescador-dia)', unidade: 'kg/pescador-dia' },
  { valor: 'registros', rotulo: 'Registros', unidade: 'registros' },
  { valor: 'esforco', rotulo: 'Esforço (pescador-dias)', unidade: 'pescador-dias' },
  { valor: 'pescadores', rotulo: 'Informantes ativos', unidade: 'informantes' },
  { valor: 'semAtividade', rotulo: 'Dias sem atividade', unidade: 'pescador-dias' },
];

const valorDaMetrica = (p: PontoSerie, metrica: Metrica): number | null => {
  switch (metrica) {
    case 'registros': return p.registros;
    case 'rendimento': return p.rendimento;
    case 'esforco': return p.esforco;
    case 'pescadores': return p.pescadoresAtivos;
    case 'semAtividade': return p.semAtividade ?? 0;
    default: return p.kg;
  }
};

const dataBR = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

const rotuloEixo = (iso: string, agregacao: Agregacao) =>
  agregacao === 'mes' ? `${iso.slice(5, 7)}/${iso.slice(0, 4)}` : `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

const rotuloTooltip = (iso: string, agregacao: Agregacao) => {
  if (agregacao === 'mes') return `${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  if (agregacao === 'semana') return `Semana de ${dataBR(iso)}`;
  return dataBR(iso);
};

const doisDigitos = (n: number) => String(n).padStart(2, '0');

/** Faixas de piracema que cruzam a série, ancoradas em períodos que existem no eixo. */
function faixasPiracema(periodos: string[]): { x1: string; x2: string }[] {
  if (periodos.length === 0) return [];
  const primeiroAno = Number(periodos[0].slice(0, 4)) - 1;
  const ultimoAno = Number(periodos[periodos.length - 1].slice(0, 4));
  const faixas: { x1: string; x2: string }[] = [];
  for (let ano = primeiroAno; ano <= ultimoAno; ano++) {
    const inicio = `${ano}-${doisDigitos(PIRACEMA.inicio.mes)}-${doisDigitos(PIRACEMA.inicio.dia)}`;
    const anoFim = PIRACEMA.fim.mes < PIRACEMA.inicio.mes ? ano + 1 : ano;
    const fim = `${anoFim}-${doisDigitos(PIRACEMA.fim.mes)}-${doisDigitos(PIRACEMA.fim.dia)}`;
    const dentro = periodos.filter((p) => p >= inicio && p <= fim);
    if (dentro.length > 0) faixas.push({ x1: dentro[0], x2: dentro[dentro.length - 1] });
  }
  return faixas;
}

interface EvolucaoProducaoProps {
  /** Query string dos filtros globais (sem '?'), a mesma do /pesca/stats. */
  filtrosQuery: string;
  /** Faixa/legenda da piracema (defeso da bacia do Paraná) só quando toda a produção mostrada é dessa bacia (ver utils/piracema.ts). */
  mostrarPiracema: boolean;
}

const EvolucaoProducao: React.FC<EvolucaoProducaoProps> = ({ filtrosQuery, mostrarPiracema }) => {
  const [agregacao, setAgregacao] = useState<Agregacao>('semana');
  // Agregação que o backend realmente usou: "Dia" em período com mais de 400 dias vira "Semana".
  const [agregacaoUsada, setAgregacaoUsada] = useState<Agregacao>('semana');
  const [ajustada, setAjustada] = useState(false);
  const [metrica, setMetrica] = useState<Metrica>('kg');
  const [serie, setSerie] = useState<PontoSerie[]>([]);
  // De onde veio a série na tela: a da produção (/stats/evolucao) ou a de períodos sem atividade (/stats/interrupcoes).
  const [fonteSerie, setFonteSerie] = useState<'producao' | 'interrupcoes'>('producao');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [temDadosAnteriores, setTemDadosAnteriores] = useState(false);
  const [tentativa, setTentativa] = useState(0); // "Tentar de novo" refaz a busca sem mudar os filtros

  // "Dias sem atividade" vem de outro endpoint (projeto, período, pescador, colônia e área; espécie, petrecho e local não se aplicam).
  const usaInterrupcoes = metrica === 'semAtividade';
  const [filtrosIgnorados, setFiltrosIgnorados] = useState<string[]>([]);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    const url = usaInterrupcoes
      ? `/pesca/stats/interrupcoes?agregacao=${agregacao}&${filtrosQuery}`
      : `/pesca/stats/evolucao?agregacao=${agregacao}&${filtrosQuery}`;
    api.get(url)
      .then((res) => {
        if (!ativo) return;
        const bruta: any[] = res.data.data.serie || [];
        setSerie(usaInterrupcoes
          ? bruta.map((p) => ({
            periodo: p.periodo, kg: 0, rendimento: null, registros: 0, esforco: 0, kgBase: 0,
            pescadoresAtivos: p.informantesAfetados, semAtividade: p.diasSemAtividade, ambientais: p.diasAmbientais,
          }))
          : bruta);
        setFonteSerie(usaInterrupcoes ? 'interrupcoes' : 'producao');
        setFiltrosIgnorados(usaInterrupcoes ? (res.data.data.filtrosNaoAplicados || []) : []);
        setAgregacaoUsada((res.data.data.agregacao as Agregacao) || agregacao);
        setAjustada(!!res.data.data.ajustada);
        setTemDadosAnteriores(true);
        setErro(null);
      })
      // mantém a série anterior na tela (não apaga) e avisa que está desatualizada
      .catch((e) => { if (ativo) setErro(e?.response?.data?.message || 'Falha ao carregar a evolução.'); })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [agregacao, filtrosQuery, tentativa, usaInterrupcoes]);

  const unidade = METRICAS.find((m) => m.valor === metrica)!.unidade;

  const dados = useMemo(() => {
    // Enquanto a série da outra fonte ainda está na tela (troca de métrica em andamento), não desenha nada.
    if (fonteSerie !== (usaInterrupcoes ? 'interrupcoes' : 'producao')) return [];
    const valores = serie.map((p) => valorDaMetrica(p, metrica));
    return serie.map((p, i) => {
      let media: number | null = null;
      // Média móvel é da produção; "Dias sem atividade" mostra os dias e, em outra linha, os ambientais.
      if (agregacaoUsada === 'dia' && !usaInterrupcoes) {
        const ini = Math.max(0, i - JANELA_MEDIA_MOVEL + 1);
        if (metrica === 'rendimento') {
          // Ponderada pelo esforço, como o card: Σ kg (registros com pescadores) ÷ Σ pescador-dias dos 7 dias.
          const janela = serie.slice(ini, i + 1);
          const esforco = janela.reduce((a, q) => a + q.esforco, 0);
          media = esforco > 0 ? janela.reduce((a, q) => a + q.kgBase, 0) / esforco : null;
        } else {
          const janela = valores.slice(ini, i + 1).filter((v): v is number => v != null);
          media = janela.length ? janela.reduce((a, v) => a + v, 0) / janela.length : null;
        }
      }
      return { periodo: p.periodo, valor: valores[i], media, ambientais: p.ambientais ?? null };
    });
  }, [serie, metrica, agregacaoUsada, fonteSerie, usaInterrupcoes]);

  const faixas = useMemo(() => (mostrarPiracema ? faixasPiracema(serie.map((p) => p.periodo)) : []), [serie, mostrarPiracema]);

  const formatarValor = (v: any) =>
    v == null ? 'n/d' : metrica === 'kg' ? formatarKg(v) : metrica === 'rendimento' ? formatarRendimento(v) : formatarNumero(v, 0);

  const botao = (ativo: boolean): React.CSSProperties => ({
    padding: '4px 12px', borderRadius: '14px', border: ativo ? '1px solid #00c3ff' : '1px solid #ddd',
    background: ativo ? '#eef9ff' : '#fff', color: ativo ? '#0a6c8f' : '#555', cursor: 'pointer', fontSize: '0.8rem', fontWeight: ativo ? 700 : 500,
  });

  return (
    <div className={styles.sectionCard} style={{ marginTop: '10px', minHeight: '450px' }}>
      <div className={styles.sectionHeader}>Evolução da produção</div>

      <div style={{ padding: '0 20px' }}>
        <AvisoDadosDesatualizados erro={!!erro} comDadosAnteriores={temDadosAnteriores} detalhe={erro} carregando={carregando} onTentarDeNovo={() => setTentativa((t) => t + 1)} />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', padding: '0 20px 8px' }}>
        <div style={{ display: 'flex', gap: '6px' }} role="group" aria-label="Agregação">
          {AGREGACOES.map((a) => (
            <button key={a.valor} type="button" onClick={() => setAgregacao(a.valor)} style={botao(agregacaoUsada === a.valor)}>{a.rotulo}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }} role="group" aria-label="Métrica">
          {METRICAS.map((m) => (
            <button key={m.valor} type="button" onClick={() => setMetrica(m.valor)} style={botao(metrica === m.valor)}>{m.rotulo}</button>
          ))}
        </div>
      </div>

      {ajustada && agregacao === 'dia' && (
        <p role="status" style={{ margin: '0 20px 6px', fontSize: '0.78rem', color: '#777' }}>
          Período com mais de 400 dias: exibindo por Semana para o gráfico continuar legível. Escolha um período menor para ver por Dia.
        </p>
      )}

      {usaInterrupcoes && (
        <p role="note" style={{ margin: '0 20px 6px', fontSize: '0.78rem', color: '#777' }}>
          Pescador-dias em períodos sem atividade (período em andamento conta até hoje). Considera projeto, período, pescador, colônia e área; espécie, petrecho, local e "somente validados" não se aplicam.
          {filtrosIgnorados.length > 0 && <span style={{ color: '#8a5a00' }}> Filtros ativos ignorados: {filtrosIgnorados.join(', ')}.</span>}
        </p>
      )}

      <CartaoExpansivel titulo="Evolução da produção" alturaNormal={380}>
        {(altura) => (
          <div style={{ width: '100%', height: altura, padding: '10px 20px', boxSizing: 'border-box', opacity: carregando ? 0.6 : 1 }}>
            {!carregando && dados.length === 0 ? (
              erro
                ? <div style={{ padding: '20px' }} />
                : <p style={{ padding: '20px', color: '#999' }}>{usaInterrupcoes ? 'Nenhum período sem atividade para os filtros atuais.' : 'Nenhuma produção para os filtros atuais.'}</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dados} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  {faixas.map((f) => (
                    <ReferenceArea key={`${f.x1}-${f.x2}`} x1={f.x1} x2={f.x2} fill="#7c8cf8" fillOpacity={0.14} ifOverflow="hidden" />
                  ))}
                  <XAxis
                    dataKey="periodo"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#666' }}
                    tickFormatter={(v: string) => rotuloEixo(v, agregacaoUsada)}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    minTickGap={12}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#666' }}
                    tickFormatter={(v: any) => (metrica === 'rendimento' ? formatarNumero(v, 1) : formatarNumero(v, 0))}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    labelFormatter={(v: any) => rotuloTooltip(String(v), agregacaoUsada)}
                    formatter={(val: any, nome: any) => [`${formatarValor(val)} ${unidade}`, nome === 'media' ? `Média móvel de ${JANELA_MEDIA_MOVEL} dias` : nome === 'ambientais' ? 'Por condições ambientais' : METRICAS.find((m) => m.valor === metrica)!.rotulo]}
                  />
                  <Line type="monotone" dataKey="valor" name="valor" stroke="#00c3ff" strokeWidth={agregacaoUsada === 'dia' ? 2 : 3} dot={{ r: agregacaoUsada === 'dia' ? 2 : 4, fill: '#00c3ff' }} activeDot={{ r: 6 }} connectNulls />
                  {usaInterrupcoes && (
                    <Line type="monotone" dataKey="ambientais" name="ambientais" stroke="#f5a623" strokeWidth={2.5} strokeDasharray="6 4" dot={false} connectNulls />
                  )}
                  {agregacaoUsada === 'dia' && !usaInterrupcoes && (
                    <Line type="monotone" dataKey="media" name="media" stroke="#f75a68" strokeWidth={2.5} strokeDasharray="6 4" dot={false} connectNulls />
                  )}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        )}
      </CartaoExpansivel>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', padding: '0 20px 14px', fontSize: '0.78rem', color: '#666' }}>
        {mostrarPiracema && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: 14, height: 14, background: '#7c8cf8', opacity: 0.35, borderRadius: 3, display: 'inline-block' }} />
            {PIRACEMA.rotulo}
          </span>
        )}
        {usaInterrupcoes && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: 18, borderTop: '2.5px dashed #f5a623', display: 'inline-block' }} />
            Por condições ambientais
          </span>
        )}
        {agregacaoUsada === 'dia' && !usaInterrupcoes && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: 18, borderTop: '2.5px dashed #f75a68', display: 'inline-block' }} />
            Média móvel de {JANELA_MEDIA_MOVEL} dias{metrica === 'rendimento' ? ' (ponderada pelo esforço)' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

export default EvolucaoProducao;
